import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, UserCheck, QrCode } from "lucide-react";
import { ResultBanner, TicketProgress, TicketStatus } from "@/components/tickets/TicketUI";
import { cn } from "@/lib/utils";

/* Calm, fixed-size product demos. Nothing here ever changes height: states are stacked in the same
   grid cell and cross-faded (opacity + a few px of travel), so the page never jumps. */

const FADE = { duration: 0.55, ease: [0.2, 0.8, 0.2, 1] };

/** Stack children on top of each other; only `active` is visible. Height = tallest child. */
const Layers = ({ active, children, className }) => {
  const reduce = useReducedMotion();
  return (
    <div className={cn("grid [&>*]:col-start-1 [&>*]:row-start-1", className)}>
      {children.map((child, i) => (
        <motion.div key={i} initial={false} animate={active === i ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 4 }} transition={FADE} className={cn(active !== i && "pointer-events-none")} aria-hidden={active !== i}>
          {child}
        </motion.div>
      ))}
    </div>
  );
};

/** Loop through [phase, ms] steps; stays on the last step when reduced motion is on. */
const useSequence = (steps, { reduce }) => {
  const [i, setI] = useState(reduce ? steps.length - 1 : 0);
  useEffect(() => {
    if (reduce) return undefined;
    let idx = 0; let t;
    const step = () => { idx = (idx + 1) % steps.length; setI(idx); t = setTimeout(step, steps[idx][1]); };
    t = setTimeout(step, steps[0][1]);
    return () => clearTimeout(t);
  }, [reduce, steps]);
  return steps[i][0];
};

/* Deterministic pseudo-QR so the demo looks real without a backend call. */
const qrCells = (size = 25, seed = 7) => {
  const cells = [];
  let x = seed;
  const rnd = () => { x = (x * 1103515245 + 12345) & 0x7fffffff; return x / 0x7fffffff; };
  const finder = (r, c) => (r < 7 && c < 7) || (r < 7 && c >= size - 7) || (r >= size - 7 && c < 7);
  for (let r = 0; r < size; r += 1) for (let c = 0; c < size; c += 1) {
    if (finder(r, c)) {
      const rr = r < 7 ? r : r - (size - 7); const cc = c < 7 ? c : c - (size - 7);
      const ring = rr === 0 || rr === 6 || cc === 0 || cc === 6; const core = rr >= 2 && rr <= 4 && cc >= 2 && cc <= 4;
      if (ring || core) cells.push([r, c]);
    } else if (rnd() > 0.55) cells.push([r, c]);
  }
  return cells;
};

export const FakeQr = ({ className }) => {
  const cells = useMemo(() => qrCells(), []);
  return (
    <svg viewBox="0 0 25 25" className={cn("h-full w-full", className)} aria-hidden="true" shapeRendering="crispEdges">
      {cells.map(([r, c]) => <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="currentColor" />)}
    </svg>
  );
};

const PHONE_STEPS = [["reserved", 5200], ["confirmed", 5200]];

/** Beat 1 – the guest's phone: Reservert (Vipps instructions) → Billetten er klar (QR). Same height in both states. */
export const PhoneTicket = ({ className }) => {
  const reduce = useReducedMotion();
  const status = useSequence(PHONE_STEPS, { reduce });
  const confirmed = status === "confirmed";
  return (
    <div className={cn("relative mx-auto w-[260px] sm:w-[272px]", className)} data-testid="arranger-demo-phone" data-state={status}>
      <div className="rounded-[40px] border border-paper/15 bg-ink/80 p-2.5 shadow-lift">
        <div className="relative overflow-hidden rounded-[32px] bg-background text-foreground">
          <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-ink" aria-hidden="true" />
          <div className="px-5 pb-6 pt-11">
            <p className="t-mono text-muted-foreground">2 billettar · KA4821</p>
            <div className="mt-2 h-6"><Layers active={confirmed ? 1 : 0}>{[<TicketStatus key="r" status="reserved" />, <TicketStatus key="c" status="confirmed" />]}</Layers></div>
            <div className="mt-2 h-[52px]">
              <Layers active={confirmed ? 1 : 0}>{[
                <h3 key="a" className="font-display text-[20px] font-semibold leading-tight tracking-[-0.02em]">Reservert – betal innan fristen</h3>,
                <h3 key="b" className="font-display text-[20px] font-semibold leading-tight tracking-[-0.02em]">Billetten er klar</h3>,
              ]}</Layers>
            </div>
            <p className="mt-1 text-[12px] text-muted-foreground">Konsert i King Oscar Amfi · laur. 14. nov kl. 19:30</p>
            <TicketProgress mode="vipps_manual" status={status} className="mt-4 [&_.t-mono-sm]:text-[8px]" />
            <div className="mt-5 h-[232px]">
              <Layers active={confirmed ? 1 : 0}>{[
                <div key="pay" className="divide-y divide-border border-y border-border">
                  {[["Vipps til", "123 45 678"], ["Beløp", "500 kr"], ["Melding", "KA4821"]].map(([l, v]) => (
                    <div key={l} className="flex items-baseline justify-between py-3"><span className="t-mono text-muted-foreground">{l}</span><span className="font-display text-[18px] font-semibold tabular tracking-[-0.02em]">{v}</span></div>
                  ))}
                  <p className="py-3 text-[11px] text-muted-foreground">Frist: 47 t 12 min att · tel ned av seg sjølv</p>
                </div>,
                <div key="qr" className="rounded-[16px] border border-border bg-card p-4">
                  <p className="t-mono text-muted-foreground">Vis denne ved inngangen</p>
                  <div className="mx-auto mt-3 w-[132px] text-foreground"><FakeQr /></div>
                  <p className="mt-3 text-center text-[11px] text-muted-foreground">Lagra i kalenderen · fungerer utan nett</p>
                </div>,
              ]}</Layers>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ORDER_STEPS = [[0, 3600], [1, 450], [2, 4200]]; // waiting · pressing · confirmed

/** Beat 2 – the organizer marks a payment as received. The toast has reserved space, so nothing moves. */
export const OrdersMini = ({ className }) => {
  const reduce = useReducedMotion();
  const phase = useSequence(ORDER_STEPS, { reduce });
  const rows = [
    { name: "Kari Nordmann", code: "KA4821", qty: 2, amount: "500 kr", status: phase === 2 ? "confirmed" : "reserved", live: true },
    { name: "Ola Hansen", code: "BN0372", qty: 1, amount: "250 kr", status: "confirmed" },
    { name: "Siri Testdal", code: "TR9150", qty: 3, amount: "750 kr", status: "waitlist" },
  ];
  return (
    <div className={cn("dark w-full", className)} data-testid="arranger-demo-orders" data-phase={phase}>
      <div className="relative overflow-hidden rounded-[24px] border border-paper/10 bg-background p-4 text-foreground sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div><div className="t-mono text-muted-foreground">Bestillingar</div><div className="mt-1 font-display text-[18px] font-semibold leading-none">Konsert i amfiet</div></div>
          <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground"><span className="live-dot" aria-hidden="true" />Live</span>
        </div>
        <ul className="mt-4 divide-y divide-border border-y border-border">
          {rows.map((r) => (
            <li key={r.code} className="flex h-[66px] items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="truncate text-[14px] font-medium">{r.name}</span>
                  <span className="relative h-6 w-[118px] shrink-0"><Layers active={r.status === "confirmed" ? 1 : r.status === "waitlist" ? 2 : 0} className="absolute inset-0">{[<TicketStatus key="r" status="reserved" />, <TicketStatus key="c" status="confirmed" />, <TicketStatus key="w" status="waitlist" />]}</Layers></span>
                </div>
                <div className="mt-0.5 text-[12px] text-muted-foreground tabular">{r.code} · {r.qty} {r.qty === 1 ? "billett" : "billettar"} · {r.amount}</div>
              </div>
              {r.live && (
                <motion.span initial={false} animate={{ scale: phase === 1 ? 0.94 : 1, opacity: phase === 2 ? 0 : 1 }} transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }} className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[9px] bg-foreground px-3 text-[13px] font-medium text-background" aria-label="Motteke"><Check className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Motteke</span></motion.span>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-4 h-11">
          <motion.div initial={false} animate={phase === 2 ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 6 }} transition={FADE} className="flex h-11 min-w-0 items-center gap-3 rounded-[12px] bg-foreground px-4 text-[13px] text-background" role="status" aria-live="polite">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sol text-ink"><Check className="h-3 w-3" strokeWidth={3} /></span>
            <span className="min-w-0 flex-1 truncate">Betaling registrert – billetten er sendt til Kari</span>
            <span className="shrink-0 text-background/70">Angre</span>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

/* Door-scan choreography: approach → lock-on + sweep → verdict → release. Every beat is time-boxed, so the card never
   changes size; only the guest's phone, the reticle and the verdict move. */
const SCAN_STEPS = [[0, 1500], [1, 1700], [2, 3400], [3, 900]]; // approach · scanning · verdict · release
const SCAN_EASE = [0.16, 1, 0.3, 1];
const PHONE_POSE = {
  0: { x: "0%", y: "0%", scale: 1, rotate: -2, opacity: 1 },                     // arrives in the frame
  1: { x: "0%", y: "0%", scale: 1, rotate: -1, opacity: 1 },                     // held still while the line sweeps
  2: { x: "0%", y: "0%", scale: 0.985, rotate: -1, opacity: 1 },                 // settles on the verdict
  3: { x: "62%", y: "70%", scale: 0.64, rotate: -16, opacity: 0 },               // lowered again – ready for the next guest
};
const PHONE_FROM = { x: "62%", y: "70%", scale: 0.64, rotate: -16, opacity: 0 };

/** Beat 3 – the QR is scanned in the door. Camera frame is a fixed aspect; the verdict area has a fixed height. */
export const QrScanDemo = ({ className }) => {
  const reduce = useReducedMotion();
  const phase = useSequence(SCAN_STEPS, { reduce });
  const verdict = phase >= 2;
  const phoneTransition = phase === 0 ? { duration: 1.3, ease: SCAN_EASE } : phase === 3 ? { duration: 0.75, ease: [0.5, 0, 0.75, 0] } : { duration: 0.5, ease: SCAN_EASE };
  return (
    <div className={cn("dark w-full", className)} data-testid="arranger-demo-scan" data-phase={phase}>
      <div className="overflow-hidden rounded-[24px] border border-paper/10 bg-background text-foreground">
        <div className="relative aspect-[4/3] overflow-hidden bg-ink">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--paper)/0.10),transparent_65%)]" aria-hidden="true" />
          {/* the guest's phone, brought up to the camera and held in the frame */}
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={reduce ? PHONE_POSE[1] : PHONE_FROM}
              animate={reduce ? PHONE_POSE[1] : (phase === 0 && !reduce ? PHONE_POSE[0] : PHONE_POSE[phase])}
              transition={phoneTransition}
              className="w-[46%] rounded-[14px] bg-paper p-3 shadow-lift will-change-transform"
            >
              <motion.div animate={reduce || phase !== 1 ? { y: 0 } : { y: [0, -1.5, 0, 1, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}>
                <div className="text-ink"><FakeQr /></div>
                <p className="mt-2 text-center text-[9px] font-medium tracking-[0.12em] text-ink/70">KA4821 · 2 BILLETTAR</p>
              </motion.div>
            </motion.div>
          </div>
          <div className="pointer-events-none absolute inset-0 bg-ink/35 [mask-image:radial-gradient(ellipse_at_center,transparent_36%,black_38%)]" aria-hidden="true" />
          {/* reticle: breathes while waiting, locks on when the code is in frame, turns green on the verdict */}
          <div className="absolute left-1/2 top-1/2 aspect-[7/6] w-[58%] -translate-x-1/2 -translate-y-1/2">
          <motion.div
            initial={false}
            animate={phase === 0 ? { scale: 1.06, opacity: 0.7 } : phase === 1 ? { scale: 1, opacity: 1 } : phase === 2 ? { scale: 0.98, opacity: 1 } : { scale: 1.06, opacity: 0.6 }}
            transition={{ duration: 0.6, ease: SCAN_EASE }}
            style={{ "--reticle": verdict ? "hsl(var(--status-confirmed))" : "hsl(var(--paper))" }}
            className="reticle h-full w-full"
          >
            <i />
            {/* one calm sweep across the code while scanning */}
            <motion.span
              initial={false}
              animate={phase === 1 && !reduce ? { top: ["10%", "88%", "10%"], opacity: [0, 0.95, 0.95, 0] } : { opacity: 0 }}
              transition={phase === 1 ? { duration: 1.6, ease: "easeInOut", times: [0, 0.5, 1] } : { duration: 0.3 }}
              className="absolute left-[8%] right-[8%] h-[2px] bg-sol shadow-[0_0_14px_hsl(var(--sol)/0.85)]"
              style={{ top: "10%" }}
            />
          </motion.div>
          </div>
          {/* capture flash + verdict */}
          <motion.div initial={false} animate={phase === 2 ? { opacity: [0.35, 0] } : { opacity: 0 }} transition={{ duration: 0.7, ease: "easeOut" }} className="pointer-events-none absolute inset-0 bg-paper" aria-hidden="true" />
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <motion.span initial={false} animate={phase === 2 ? { scale: [0.7, 1.9], opacity: [0.5, 0] } : { scale: 0.7, opacity: 0 }} transition={{ duration: 1.1, ease: "easeOut" }} className="absolute inset-0 rounded-full border-2 border-[hsl(var(--status-confirmed))]" aria-hidden="true" />
            <motion.div initial={false} animate={phase === 2 ? { opacity: 1, scale: 1 } : { opacity: 0, scale: reduce ? 1 : 0.5 }} transition={phase === 2 ? { type: "spring", stiffness: 420, damping: 22, mass: 0.8 } : { duration: 0.3 }} className="flex h-16 w-16 items-center justify-center rounded-full bg-status-confirmed text-paper shadow-lift" aria-hidden="true"><Check className="h-8 w-8" strokeWidth={3} /></motion.div>
          </div>
          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3 text-paper"><span className="t-mono-sm text-paper/70">Konsert i amfiet</span><span className="rounded-full bg-ink/60 px-2 py-0.5 text-[11px] tabular backdrop-blur">{verdict ? "143" : "141"} / 143 inne</span></div>
        </div>
        <div className="h-[168px] p-4" aria-live="polite">
          <Layers active={verdict ? 1 : 0} className="h-full">{[
            <div key="s" className="flex h-[136px] items-center gap-3 rounded-[16px] border border-dashed border-input p-4 text-[13px] text-muted-foreground"><QrCode className="h-5 w-5 shrink-0" /> {phase === 0 ? "Klar. Hald QR-koden i ramma." : "Skannar … Svaret kjem med lyd og vibrasjon."}</div>,
            <ResultBanner key="v" variant="ok" title="Gyldig billett – slepp inn" live={false} className="h-[136px] p-4">
              <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3"><span className="font-display text-[18px] font-semibold leading-tight">Kari Nordmann</span><span className="text-[12px] text-muted-foreground tabular">KA4821 · 2 × Vaksen</span></div>
              <div className="mt-3 flex gap-2"><span className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-[10px] bg-foreground text-[13px] font-medium text-background"><UserCheck className="h-4 w-4" /> Slepp inn alle 2</span><span className="inline-flex h-10 items-center rounded-[10px] border border-border px-3 text-[13px]">+1</span></div>
            </ResultBanner>,
          ]}</Layers>
        </div>
      </div>
    </div>
  );
};
